import { ChoiceBox, ChoiceBoxDescription, ChoiceBoxItem, ChoiceBoxLabel } from "@/components/ui/choice-box.tsx";
import { BoltIcon, UsersIcon } from "@heroicons/react/24/outline";
import { CheckIcon } from "@heroicons/react/20/solid";
import { useI18nextInstance } from "@squide/i18next";
import { useCallback, useId } from "react";
import type { Selection } from "react-aria-components";
import { useTranslation } from "react-i18next";
import { segmentsI18NextInstanceKey } from "../i18n/i18next.ts";

const RULE_BASED_KEY = "rule-based";
const HAND_PICKED_KEY = "hand-picked";

const MODE_KEYS = [RULE_BASED_KEY, HAND_PICKED_KEY];

const ICON_COLOR_VARS = "[--mode-icon-selected:var(--color-primary-subtle-fg)] [--mode-icon:var(--color-muted-fg)]";

const SELECTED_CARD_TEXT = "[&_[data-slot=choice-box-item]]:[--choice-box-fg:var(--color-fg)] [&_[data-slot=choice-box-item][data-selected]_[slot=description]]:text-muted-fg [&_[data-slot=check-indicator]]:text-primary";

interface ModeIconProps {
    Icon: typeof BoltIcon;
    isSelected: boolean;
}

function ModeIcon({ Icon, isSelected }: ModeIconProps) {
    return <Icon aria-hidden="true" className={isSelected ? "text-(--mode-icon-selected)" : "text-(--mode-icon)"} />;
}

interface SelectedMarkProps {
    isReadOnly?: boolean;
    label: string;
}

function SelectedMark({ isReadOnly, label }: SelectedMarkProps) {
    return (
        <>
            <CheckIcon data-slot="check-indicator" aria-hidden="true" />
            {isReadOnly && <span className="sr-only">{label}</span>}
        </>
    );
}

export interface SegmentModeChooserProps {
    isRuleBased: boolean;
    isReadOnly?: boolean;
    onChange: (isRuleBased: boolean) => void;
}

export function SegmentModeChooser({ isRuleBased, isReadOnly, onChange }: SegmentModeChooserProps) {
    const i18nextInstance = useI18nextInstance(segmentsI18NextInstanceKey);
    const { t } = useTranslation("SegmentModeChooser", { i18n: i18nextInstance });
    const headingId = useId();

    const handleSelectionChange = useCallback(
        (keys: Selection) => {
            if (keys !== "all") {
                onChange(keys.has(RULE_BASED_KEY));
            }
        },
        [onChange]
    );

    const selectedKey = isRuleBased ? RULE_BASED_KEY : HAND_PICKED_KEY;

    return (
        <div className="flex flex-col gap-2">
            <p id={headingId} className="text-fg text-sm font-medium">
                {t("heading")}
            </p>
            <ChoiceBox aria-labelledby={headingId} className={`${ICON_COLOR_VARS} ${SELECTED_CARD_TEXT}`} columns={2} gap={3} disallowEmptySelection selectedKeys={[selectedKey]} onSelectionChange={handleSelectionChange} disabledKeys={isReadOnly ? MODE_KEYS : undefined}>
                <ChoiceBoxItem id={RULE_BASED_KEY} textValue={t("ruleBased.title")} className={({ isSelected }) => (isSelected ? "" : "bg-card")}>
                    <ModeIcon Icon={BoltIcon} isSelected={isRuleBased} />
                    <ChoiceBoxLabel>{t("ruleBased.title")}</ChoiceBoxLabel>
                    <ChoiceBoxDescription>{t("ruleBased.description")}</ChoiceBoxDescription>
                    {isRuleBased && <SelectedMark isReadOnly={isReadOnly} label={t("selected")} />}
                </ChoiceBoxItem>
                <ChoiceBoxItem id={HAND_PICKED_KEY} textValue={t("handPicked.title")} className={({ isSelected }) => (isSelected ? "" : "bg-card")}>
                    <ModeIcon Icon={UsersIcon} isSelected={!isRuleBased} />
                    <ChoiceBoxLabel>{t("handPicked.title")}</ChoiceBoxLabel>
                    <ChoiceBoxDescription>{t("handPicked.description")}</ChoiceBoxDescription>
                    {!isRuleBased && <SelectedMark isReadOnly={isReadOnly} label={t("selected")} />}
                </ChoiceBoxItem>
            </ChoiceBox>
        </div>
    );
}
